import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { tarifasApi, TarifaDieta } from '../lib/api'
import { useToast } from '../components/Toast'

const MONO = { fontFamily: 'JetBrains Mono' } as const

export default function AdminTarifas() {
  const { user, logout } = useAuth()
  const { showToast } = useToast()

  const [tarifas, setTarifas] = useState<TarifaDieta[]>([])
  const [defaultPrice, setDefaultPrice] = useState<number>(0)
  const [editing, setEditing] = useState<Record<number, string>>({})
  const [loading, setLoading] = useState(true)
  const [savingDefault, setSavingDefault] = useState(false)
  const [defaultInput, setDefaultInput] = useState('')

  async function refresh() {
    setLoading(true)
    try {
      const [tar, def] = await Promise.all([
        tarifasApi.listar(),
        tarifasApi.getDefault(),
      ])
      setTarifas(tar)
      setDefaultPrice(def.price_unit_default)
      setDefaultInput(String(def.price_unit_default))
    } catch (err: any) {
      showToast(`Error cargando tarifas: ${err.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  async function guardarTarifa(id: number) {
    const v = editing[id]
    if (v === undefined) return
    const trimmed = v.trim()
    const precio_unit = trimmed === '' ? null : Number(trimmed)
    if (precio_unit !== null && (isNaN(precio_unit) || precio_unit < 0)) {
      showToast('Precio inválido (vacío para usar default, o número ≥ 0)', 'error')
      return
    }
    try {
      await tarifasApi.actualizar(id, precio_unit)
      showToast(`Tarifa actualizada`, 'success')
      setEditing((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
      await refresh()
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message
      showToast(detail, 'error')
    }
  }

  async function guardarDefault() {
    const v = Number(defaultInput)
    if (isNaN(v) || v < 0) {
      showToast('Precio default inválido (≥ 0)', 'error')
      return
    }
    setSavingDefault(true)
    try {
      await tarifasApi.setDefault(v)
      showToast(`Precio default actualizado a $${v.toFixed(2)}`, 'success')
      await refresh()
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message
      showToast(detail, 'error')
    } finally {
      setSavingDefault(false)
    }
  }

  const isAdmin = user?.rol === 'admin'

  return (
    <div className="min-h-screen">
      <header
        className="sticky top-0 z-30 px-4 md:px-6 py-3 flex items-center gap-3"
        style={{
          background: 'linear-gradient(120deg,#143C78,#1E1E3C)',
          borderBottom: '1px solid rgba(140,170,230,.18)',
        }}
      >
        <img
          src="/avante-logo-white.png"
          alt="Avante"
          style={{ height: 30 }}
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold leading-tight truncate" style={{ fontFamily: 'Space Grotesk' }}>
            Tarifas de dietas
          </h1>
          <p className="text-xs text-[#94A0CD] truncate">{user?.nombre_completo}</p>
        </div>
        <button onClick={logout} className="text-sm text-[#94A0CD] hover:text-white shrink-0">
          Salir
        </button>
      </header>
      <main className="p-4 space-y-6 mx-auto" style={{ maxWidth: 900 }}>
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
          <p className="text-blue-900">
            Precio que se carga a la cuenta interna del paciente al entregarle
            cada comida. Dejar el campo vacío = usar el precio default.
          </p>
        </div>

        <section className="bg-white rounded-lg shadow p-4">
          <h2 className="font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Precio default</h2>
          <p className="text-xs text-gray-500 mb-2">
            Se aplica a toda dieta sin precio propio.
            {!isAdmin && ' (Solo admin puede modificarlo)'}
          </p>
          <div className="flex gap-2 items-center">
            <span className="text-sm">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={defaultInput}
              onChange={(e) => setDefaultInput(e.target.value)}
              disabled={!isAdmin}
              className="border rounded p-2 w-32"
              style={MONO}
            />
            {isAdmin && (
              <button
                onClick={guardarDefault}
                disabled={savingDefault || Number(defaultInput) === defaultPrice}
                className="px-4 text-white rounded font-semibold disabled:opacity-50"
                style={{ minHeight: 44, borderRadius: 14, background: 'linear-gradient(120deg,#006EB4,#143C78)', boxShadow: '0 10px 22px -8px rgba(0,110,180,.7)' }}
              >
                {savingDefault ? '...' : 'Guardar'}
              </button>
            )}
          </div>
        </section>

        <section>
          <h2 className="text-[11px] uppercase tracking-[0.12em] font-semibold mb-2" style={{ color: '#8B97C8' }}>
            Tarifas por dieta ({tarifas.length})
          </h2>
          {loading && <p className="text-gray-500">Cargando...</p>}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">Código</th>
                  <th className="text-left p-2">Nombre</th>
                  <th className="text-center p-2">Activo</th>
                  <th className="text-right p-2">Precio actual</th>
                  <th className="text-right p-2">Editar</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {tarifas.map((t) => {
                  const editValue = editing[t.id]
                  const isEditing = editValue !== undefined
                  const efectivo = Number(t.precio_efectivo).toFixed(2)
                  const customo = t.precio_unit !== null
                  return (
                    <tr key={t.id} className="border-t">
                      <td className="p-2 font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{t.codigo}</td>
                      <td className="p-2">{t.nombre}</td>
                      <td className="p-2 text-center" style={{ color: t.activo ? '#2FE08A' : '#7F8BBB' }}>
                        {t.activo ? '✓' : '—'}
                      </td>
                      <td className="p-2 text-right" style={MONO}>
                        <span style={{ color: customo ? '#7FD4FF' : undefined }}>${efectivo}</span>{' '}
                        {!customo && (
                          <span className="text-xs text-gray-400">(default)</span>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="(default)"
                          value={isEditing ? editValue : (t.precio_unit ?? '').toString()}
                          onChange={(e) =>
                            setEditing((prev) => ({
                              ...prev,
                              [t.id]: e.target.value,
                            }))
                          }
                          className="border rounded p-1 w-24 text-right"
                          style={{ ...MONO, minHeight: 40 }}
                        />
                      </td>
                      <td className="p-2">
                        {isEditing && (
                          <button
                            onClick={() => guardarTarifa(t.id)}
                            className="text-xs text-white px-3 rounded font-semibold"
                            style={{ minHeight: 40, borderRadius: 12, background: 'linear-gradient(120deg,#006EB4,#143C78)' }}
                          >
                            Guardar
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Dejar el input vacío al guardar = usar el precio default. Valor 0 =
            cobrar $0 explícitamente para esa dieta.
          </p>
        </section>
      </main>
    </div>
  )
}
